import { API, graphqlOperation } from "aws-amplify";

import { carrierByActiveByName } from "../graphql/queries";

/**
 * The yard's active carrier list, shared across every mounted CarrierNameInput.
 *
 * Two reasons this is module state rather than component state:
 *
 * 1. A TabbedForm renders its hidden tabs too, so a form with the carrier name
 *    on two tabs mounts two instances of the input at once. They share one
 *    request rather than pulling the list twice on every form load.
 * 2. A carrier created anywhere in the session — the input's own dialog or the
 *    manager's Carriers form — has to reach those instances. Otherwise the
 *    carrier the user just created is missing from the dropdown until a reload.
 */
let carrierListCache = null;

// Long enough that a form with the input on two tabs, or a user moving between
// forms, still shares one request; short enough that a carrier archived or
// created by somebody else shows up without a page reload. Before this the
// entry lived for the whole session, so a tab left open since the morning went
// on offering carriers another manager had archived hours earlier — and never
// saw one a colleague, or the gate kiosk, had created.
const CACHE_TTL_MS = 5 * 60 * 1000;

// Carriers created during this session, kept alongside the cache instead of
// folded into it: the GSI behind carrierByActiveByName is eventually consistent,
// so re-fetching right after a create can still come back without the new row.
// Merging locally makes the carrier selectable regardless.
let createdCarriers = [];

// The yard createdCarriers belongs to, tracked separately from carrierListCache
// because the two have different lifetimes: the cache is null before the first
// input mounts and again after a failed fetch, while a publish can land in
// either of those windows. Clearing on a null cache would drop the publish; the
// yard id is what actually says whether the entries are still ours to merge.
let createdCarriersYardId = null;

// Mounted inputs, so a carrier created in one place reaches the others.
const listeners = new Set();

const withCreatedCarriers = (items) => {
  if (!createdCarriers.length) return items;
  const known = new Set(items.map((c) => c.id));
  return [...items, ...createdCarriers.filter((c) => !known.has(c.id))];
};

export const fetchCarrierList = (reloadYardId) => {
  if (createdCarriersYardId != null && createdCarriersYardId !== reloadYardId) {
    // A different yard's carriers are not ours to merge.
    createdCarriers = [];
  }
  // A publish that arrived without a yard id gets adopted by the first yard to
  // ask for the list — the same benefit of the doubt publishCreatedCarrier gives
  // a partial record.
  createdCarriersYardId = reloadYardId;

  const expired =
    carrierListCache != null && Date.now() - carrierListCache.fetchedAt >= CACHE_TTL_MS;
  if (carrierListCache?.reloadYardId !== reloadYardId || expired) {
    carrierListCache = {
      reloadYardId,
      fetchedAt: Date.now(),
      promise: API.graphql(
        graphqlOperation(carrierByActiveByName, {
          reloadYardId,
          activeBusinessName: { beginsWith: { active: "true" } },
          limit: 1000,
        }),
      )
        .then((response) => response.data.carrierByActiveByName.items)
        .catch((err) => {
          // Never cache a failure — the next mount should retry.
          carrierListCache = null;
          throw err;
        }),
    };
  }
  // Merged per call rather than baked into the cached promise, so a form that
  // mounts after a create sees the carrier without a second round trip.
  return carrierListCache.promise.then(withCreatedCarriers);
};

/**
 * Announce a newly-created carrier: records it for later mounts and hands it to
 * every input mounted now. Call this from every path that creates a Carrier —
 * a create the cache never hears about is a carrier the user cannot select.
 */
export const publishCreatedCarrier = (newCarrier) => {
  // businessName is what the input keys its choices on; without one the carrier
  // would join the list as a blank entry.
  if (!newCarrier?.id || !newCarrier.businessName) return;
  // Don't merge a carrier the yard's own query would not have returned. Checks
  // only the fields the record actually carries, so a caller that publishes a
  // partial record gets the benefit of the doubt rather than a silent no-op.
  if (newCarrier.active != null && newCarrier.active !== "true") return;
  const knownYardId = carrierListCache?.reloadYardId ?? createdCarriersYardId;
  if (
    knownYardId != null &&
    newCarrier.reloadYardId != null &&
    newCarrier.reloadYardId !== knownYardId
  )
    return;
  // First publish of the session: adopt its yard so a later publish for a
  // different one is turned away by the check above.
  if (createdCarriersYardId == null)
    createdCarriersYardId = newCarrier.reloadYardId ?? null;
  if (!createdCarriers.some((c) => c.id === newCarrier.id))
    createdCarriers = [...createdCarriers, newCarrier];
  listeners.forEach((listener) => listener(newCarrier));
};

/**
 * Drop the cached list so the next mount refetches.
 *
 * Archiving or restoring a carrier flips the `active` flag the cached query
 * filtered on, so the entry is wrong the moment it happens — an archived
 * carrier stays selectable, and selecting it writes a carrierId pointing at
 * one. Publishing does not cover it: that path only ever adds.
 */
export const invalidateCarrierList = () => {
  carrierListCache = null;
  createdCarriers = [];
};

/** Returns an unsubscribe function, for a useEffect cleanup. */
export const subscribeToCreatedCarriers = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
